import JXG from 'jsxgraph';

import timesSvg from '../assets/times.svg';
import checkSvg from '../assets/check.svg';

import {colors, flavorDescriptions, flavorTypes} from '../constants';

/**
 * This ensures entities are added to the graph in the appropriate order (e.g. authorProvided
 * flavored entities always appear below the student's guess). Due to a bug with jsxgraph, layers
 * at or above 20 are not respected. Therefore, we're leaning on a somewhat shaky assumptions to achieve this.
 *
 * The assumption is this:
 * - The highest layer element we care about is point, whose default layer is 9
 * - We only have three flavors, so we only need to create 3 layers (i.e. layerZIndex) of elements
 * - Because of this, we can use the layerZIndex as a multiplier which we multiply by 5 to get a max of 15
 * - Since the highest default layer we care about is 9, we can halve this and floor it, and add it to the
 *   result of the previous bullet point, and we get a max layer of 19
 *
 * @todo If/when layers 20 and higher are supported by jsxgraph, update to use a formula of...
 * @example
 * const getLayerForFlavoredEntityType = (type, flavor) => {
 *   const zIndex = flavorDescriptions[flavor]?.layerZIndex ?? 0;
 *   const maxEntityLayerValue = Math.max(Object.values(...JXG.Options.layer));
 *   const addend = zIndex * maxEntityLayerValue;
 *   return JXG.Options.layer[type] + addend;
 * }
 *
 * @param {string} type Entity type
 * @param {string} flavor Flavor
 * @returns {number} Layer
 */
export function getLayerForFlavoredEntityType(type, flavor) {
  const defaultLayer = JXG.Options.layer[type];
  const scaledLayer = Math.floor(defaultLayer / 2);
  const layerZIndex = flavorDescriptions[flavor]?.layerZIndex ?? 0;
  const addend = layerZIndex * 5;
  return scaledLayer + addend;
}

export function createPoint({
  board,
  coords: [x, y],
  flavor,
  color,
  layer = getLayerForFlavoredEntityType('point', flavor)
}) {
  return board.create('point', [x, y], {
    fixed: true,
    snapToGrid: true,
    showInfoBox: false,
    strokeWidth: 0,
    highlightStrokeWidth: 0,
    fillColor: color || flavorDescriptions[flavor].color,
    highlightFillColor: color || flavorDescriptions[flavor].color,
    withLabel: false,
    size: 6,
    layer,
    tabindex: null
  });
}

export function handleAddCorrectnessIcon({board, flavor, coords: [x, y]}) {
  if (![flavorTypes.correctAnswer, flavorTypes.incorrectAnswer].includes(flavor)) {
    return null;
  }

  // eslint-disable-next-line no-unused-vars
  const [_, top, right] = board.attr.boundingbox.map((bound) => parseInt(bound, 10));

  const xPos = x !== right ? x + 0.15 : x - 0.4;
  const yPos = y !== top ? y + 0.1 : y - 0.5;

  let elementDetails;

  if (flavor === flavorTypes.correctAnswer) {
    const scale = 2;
    // These values are based on the SVG's viewbox. If you're changing the SVG, please update these values.
    const width = 0.2 * scale;
    const height = 0.16 * scale;

    elementDetails = [checkSvg, [xPos, yPos], [width, height]];
  } else if (flavor === flavorTypes.incorrectAnswer) {
    const scale = 2.25;
    // These values are based on the SVG's viewbox. If you're changing the SVG, please update these values.
    const width = 0.17 * scale;
    const height = 0.16 * scale;

    elementDetails = [timesSvg, [xPos, yPos], [width, height]];
  }

  const icon = board.create('image', elementDetails, {
    fixed: true,
    highlightFillOpacity: 1,
    tabindex: null,
    /**
     * Images have a low layer value by default, so we're going
     * to use 'point' as the base layer so it sits on top of other elements
     */
    layer: getLayerForFlavoredEntityType('point', flavor)
  });

  return icon;
}

export function buildGeometryInstance({
  board,
  entity,
  geometryElement,
  constituentElements,
  onClick,
  editOptions,
  correctnessIcon,
  // If any clean up has to be done when a geometryInstance is removed,
  // you can handle that here.
  onRemove = () => {}
}) {
  const instance = {
    entity,
    geometryElement,
    editOptions,
    onClick,
    setColor(color) {
      constituentElements.forEach((element) => {
        /**
         * While most every geometryElement only needs its stroke color changed,
         * points need their fill color changed.
         */
        const isPoint = element.elType === 'point';

        element.setAttribute({
          strokeColor: color,
          highlightStrokeColor: color,
          fillColor: isPoint ? color : null,
          highlightFillColor: isPoint ? color : null
        });
      });
    },
    remove() {
      board.removeAncestors(geometryElement);
      if (correctnessIcon) {
        correctnessIcon.remove();
      }
      onRemove();
    }
  };

  // Since JSXGraph doesn't seem to provide a click/tap handler, we simulate one
  // by hooking into the down and up events via this helper class
  instance.__clickHandler = new ClickHandler(instance, constituentElements, onClick);

  const {color} = flavorDescriptions[entity.flavor];

  instance.setColor(color);

  instance.highlight = () => {
    instance.setColor(colors.highlighted);
  };

  instance.unhighlight = () => {
    instance.setColor(color);
  };

  return instance;
}

class ClickHandler {
  static timeout = 200;

  constructor(instance, constituentElements, onClick) {
    this.instance = instance;
    this.onClick = onClick;
    this.intervalId = null;
    constituentElements.forEach((element) => {
      element.on('down', this.handleDown);
      element.on('up', this.handleUp);
    });
  }

  resetPending = () => {
    clearTimeout(this.intervalId);
    this.intervalId = null;
  };

  handleDown = () => {
    this.intervalId = setTimeout(this.resetPending, ClickHandler.timeout);
  };

  handleUp = () => {
    if (this.intervalId) {
      this.onClick(this.instance);
      this.resetPending();
    }
  };
}

/**
 * Converts an entity's elements to an array of coodinate pairs.
 *
 * @param {object} elements An entity's elements in the form of an objects
 *   with arrays or objects as their values. Leaf nodes must be a coordinate pair array,
 *   or an array of coordinate pair arrays
 * @param {Array.<Array.<number>>} _temp Temporary arg to hold the flattened value. Do not
 *   provide.
 * @returns {Array.<number>} Coordinate pairs
 */
export function toCoordsList(elements, _temp = []) {
  const values = Object.values(elements);

  values.forEach((val) => {
    if (!val) {
      return;
    }

    if (!Array.isArray(val)) {
      // eslint-disable-next-line consistent-return
      return toCoordsList(val, _temp);
    }

    if (typeof val[0] === 'number') {
      _temp.push(val);
      return;
    }

    _temp = _temp.concat(val);
  });

  return _temp;
}

export function isSameCoordinatePair(a, b) {
  return a[0] === b[0] && a[1] === b[1];
}

/**
 * Given an entity's elements object, returns whether or not the elements
 * contain the coordinate pair
 *
 * @param {object} elements Entity's elements
 * @param {Array.<number>} coords Coordinate pair
 * @returns {boolean} Whether or not the elements contain the coordinate pair
 */
export function hasCoords(elements, coords) {
  const entityCoords = toCoordsList(elements);

  return entityCoords.some((entityCoord) => isSameCoordinatePair(entityCoord, coords));
}
